import { useEffect, useMemo, useState } from "react";
import API_ENDPOINTS from "../shared/api-endpoints";
import { scrollToIslandTop } from "../shared/forms/scroll";
import { isBlank, isValidEmail, isValidPhone } from "../shared/forms/validation";
import useRecaptcha from "../shared/hooks/useRecaptcha";
import { getOrganisationList } from "../technical-support/services/contactApi";

function parseResponseBody(text) {
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function normaliseServerErrors(body) {
  if (!body) {
    return ["Unexpected error."];
  }

  if (Array.isArray(body)) {
    return body;
  }

  if (typeof body === "string") {
    return [body];
  }

  if (body.message) {
    return [body.message];
  }

  return ["Unexpected error."];
}

function getResponseCode(result) {
  return Number(result?.ResponseCode ?? result?.responseCode ?? 0);
}

function buildFieldErrors(form, config, filteredItems) {
  const errors = {};
  const organisationRequired = !config.isGasConsumerQuery;
  const exactOrganisationMatch =
    config.isGasConsumerQuery ||
    (form.organisationIdQA !== "" &&
      filteredItems.length >= 1 &&
      Number(form.organisationIdQA) === Number(filteredItems[0].id));

  if (isBlank(form.firstNameQA)) {
    errors.firstNameQA = "required";
  } else if (form.firstNameQA.length > 256) {
    errors.firstNameQA = "maxLength";
  }

  if (isBlank(form.surnameQA)) {
    errors.surnameQA = "required";
  } else if (form.surnameQA.length > 256) {
    errors.surnameQA = "maxLength";
  }

  if (isBlank(form.emailQA)) {
    errors.emailQA = "required";
  } else if (form.emailQA.length > 100) {
    errors.emailQA = "maxLength";
  } else if (!isValidEmail(form.emailQA)) {
    errors.emailQA = "email";
  }

  if (form.phoneQA && form.phoneQA.length > 32) {
    errors.phoneQA = "maxLength";
  } else if (form.phoneQA && !isValidPhone(form.phoneQA)) {
    errors.phoneQA = "phone";
  }

  if (organisationRequired) {
    if (isBlank(form.organisationQA)) {
      errors.organisationQA = "required";
    } else if (form.organisationQA.length > 256) {
      errors.organisationQA = "maxLength";
    } else if (!exactOrganisationMatch) {
      errors.organisationQA = "invalid";
    }
  } else if (form.organisationQA.length > 256) {
    errors.organisationQA = "maxLength";
  }

  if (Number(form.organisationIdQA) === -1 && isBlank(form.otherOrganisationQA)) {
    errors.otherOrganisationQA = "required";
  }

  if (form.mprnQA && form.mprnQA.length > 100) {
    errors.mprnQA = "maxLength";
  }

  if (isBlank(form.descQA)) {
    errors.descQA = "required";
  } else if (form.descQA.length > 4000) {
    errors.descQA = "maxLength";
  }

  if (config.cmsVersionRequired && isBlank(form.cmsVersionQA)) {
    errors.cmsVersionQA = "required";
  }

  if (!form.tandcQA) {
    errors.tandcQA = "required";
  }

  return errors;
}

export default function useContactQueryForm(config) {
  const [form, setForm] = useState({
    firstNameQA: "",
    surnameQA: "",
    emailQA: "",
    phoneQA: "",
    organisationQA: "",
    organisationIdQA: "",
    otherOrganisationQA: "",
    mprnQA: "",
    descQA: "",
    filesQA: [],
    tandcQA: false,
    cmsVersionQA: "",
  });
  const [organisations, setOrganisations] = useState([]);
  const [showDropdownList, setShowDropdownList] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverSideErrors, setServerSideErrors] = useState([]);
  const [submitResult, setSubmitResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const { getResponse: getRecaptchaResponse, reset: resetRecaptcha } = useRecaptcha({
    sitekey: config.captchaKey,
    onSuccess: () => setRecaptchaError(""),
  });

  useEffect(() => {
    getOrganisationList(config.isGasConsumerQuery)
      .then((items) => setOrganisations(Array.isArray(items) ? items : []))
      .catch(() => setOrganisations([]));
  }, [config.isGasConsumerQuery]);

  useEffect(() => {
    const loader = document.querySelector(".loader");
    if (loader) {
      loader.style.display = loading ? "block" : "none";
    }
  }, [loading]);

  const filteredItems = useMemo(() => {
    const query = form.organisationQA.trim();
    const other = config.allowNotListedOrganisation
      ? [
          {
            id: -1,
            shortCode: "OTHER",
            name: "Organisation not listed?",
            combinedTitle: "Organisation not listed?",
            gasSupplier: false,
          },
        ]
      : [];

    if (!query) {
      return [];
    }

    const exact = organisations.filter((item) => item.combinedTitle === query);
    if (exact.length === 1) {
      return exact;
    }

    const parts = query
      .split(/\s+/)
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);

    return other.concat(
      organisations.filter((item) =>
        parts.every((part) => (item.combinedTitle || "").toLowerCase().includes(part)),
      ),
    );
  }, [config.allowNotListedOrganisation, form.organisationQA, organisations]);

  const validationItems = useMemo(
    () =>
      [
        fieldErrors.firstNameQA ? config.labels.firstNameLabel : "",
        fieldErrors.surnameQA ? config.labels.surnameLabel : "",
        fieldErrors.emailQA ? config.labels.emailLabel : "",
        fieldErrors.phoneQA ? config.labels.telephoneLabel : "",
        (fieldErrors.organisationQA || fieldErrors.otherOrganisationQA) &&
        !config.isGasConsumerQuery
          ? config.labels.organisationLabel
          : "",
        fieldErrors.mprnQA ? config.labels.mprnLabel : "",
        fieldErrors.descQA ? config.labels.descriptionLabel : "",
        fieldErrors.tandcQA ? config.labels.disclaimerTitle : "",
        fieldErrors.cmsVersionQA ? config.cmsVersionFieldTitle : "",
        recaptchaError ? config.labels.captchaMissingLabel : "",
      ].filter(Boolean),
    [config, fieldErrors, recaptchaError],
  );

  useEffect(() => {
    if (!hasAttemptedSubmit) {
      return;
    }

    setFieldErrors(buildFieldErrors(form, config, filteredItems));
  }, [config, filteredItems, form, hasAttemptedSubmit]);

  function updateField(name, value) {
    setServerSideErrors([]);
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function selectOrganisation(item) {
    setForm((current) => ({
      ...current,
      organisationQA: item.combinedTitle,
      organisationIdQA: item.id,
      otherOrganisationQA: item.id === -1 ? current.otherOrganisationQA : "",
    }));
    setShowDropdownList(false);
  }

  function addUploadedFile(uploadedFile) {
    setServerSideErrors([]);
    setForm((current) => ({
      ...current,
      filesQA: current.filesQA.concat(uploadedFile),
    }));
  }

  function removeFileByNameGuid(nameGuid) {
    setForm((current) => ({
      ...current,
      filesQA: current.filesQA.filter((item) => item.nameGuid !== nameGuid),
    }));
  }

  function validateForm() {
    const nextFieldErrors = buildFieldErrors(form, config, filteredItems);
    const captchaResponse = getRecaptchaResponse();

    setFieldErrors(nextFieldErrors);
    setRecaptchaError(captchaResponse ? "" : "Complete captcha challenge");

    return {
      valid: !Object.keys(nextFieldErrors).length && Boolean(captchaResponse),
      captchaResponse,
    };
  }

  async function submitForm(event) {
    event.preventDefault();
    setHasAttemptedSubmit(true);
    setServerSideErrors([]);

    const { valid, captchaResponse } = validateForm();
    if (!valid) {
      scrollToIslandTop("contact-query-form-island");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.contactFormSubmit, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
          IsGasConsumerQuery: String(config.isGasConsumerQuery),
          QueryType: config.queryType,
          FirstName: form.firstNameQA,
          Surname: form.surnameQA,
          Email: form.emailQA,
          Phone: form.phoneQA,
          OrganisationId: form.organisationIdQA,
          OrganisationOther: form.otherOrganisationQA,
          Mprn: form.mprnQA,
          Description: form.descQA,
          Files: form.filesQA,
          CmsVersion: form.cmsVersionQA || null,
          CaptchaVerification: captchaResponse,
        }),
      });

      const rawBody = await response.text();
      const body = parseResponseBody(rawBody);

      if (!response.ok) {
        throw { body };
      }

      setSubmitResult(body);
      if (typeof window.logGAEvent === "function") {
        window.logGAEvent("Help & Support", "Form Submit Success", document.location.href);
      }
      scrollToIslandTop("contact-query-form-island");
    } catch (error) {
      const body = error?.body;
      if (getResponseCode(body)) {
        setSubmitResult(body);
      } else {
        setServerSideErrors(normaliseServerErrors(body));
        resetRecaptcha();
        setRecaptchaError("");
      }
      scrollToIslandTop("contact-query-form-island");
    } finally {
      setLoading(false);
    }
  }

  return {
    form,
    filteredItems,
    showDropdownList,
    setShowDropdownList,
    fieldErrors,
    serverSideErrors,
    submitResult,
    loading,
    recaptchaError,
    validationItems,
    updateField,
    selectOrganisation,
    addUploadedFile,
    removeFileByNameGuid,
    submitForm,
    getResponseCode,
  };
}
